import React, { useEffect, useRef, useState } from 'react';
import { webOrigin } from '../utils/brand';
import { shareImage as shareImageFile } from '../utils/nativeShare';
import { createPortal } from 'react-dom';
import * as htmlToImage from 'html-to-image';
import PartyStarterStoryCard from './PartyStarterStoryCard';
import ShareTemplatePicker from './share/ShareTemplatePicker';
import ScaledCard from './share/ScaledCard';
import useShareTemplates from '../hooks/useShareTemplates';

// Party Starters are pink across the app.
const PINK = '#FF00FF';

function ensureFont() {
  if (document.getElementById('pr-courier-prime')) return;
  const l = document.createElement('link');
  l.id = 'pr-courier-prime';
  l.rel = 'stylesheet';
  l.href = 'https://fonts.googleapis.com/css2?family=Courier+Prime:wght@400;700&display=swap';
  document.head.appendChild(l);
}

const dollars = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;
const shortDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).toUpperCase(); }
  catch { return ''; }
};

/**
 * Compose the pink Party Starter card props from an event. Picks the sold-out
 * variant once the floor is hit, otherwise the host card with the live floor bar.
 * The pledger variant is offered from the pledge-success flow, not here.
 */
function buildCard(event) {
  const host = (event.hostDisplayName || event.hostName || 'Someone').trim();
  const goalCents = event.fundingGoalCents || 0;
  const raisedCents = event.totalPledgedCents || 0;
  const price = event.displayPriceCents ?? event.ticketPriceCents ?? 0;
  // Phase 10 — a free Party Starter's floor is a headcount, not cash. Nobody
  // pledges a dollar, so the card counts heads and the CTA is "I'M IN".
  const isRsvpGoal = event.goalType === 'rsvp';
  const goalGuests = isRsvpGoal
    ? (event.rsvpGoal || 0)
    : (price > 0 ? Math.max(1, Math.ceil(goalCents / price)) : 0);
  const inCount = event.acceptedCount || 0;
  const away = Math.max(0, goalGuests - inCount);
  const funded = !!event.fundingGoalMetAt || (isRsvpGoal
    ? goalGuests > 0 && inCount >= goalGuests
    : goalCents > 0 && raisedCents >= goalCents);
  const place = (event.locationCity || event.locationLabel || event.city || '').toUpperCase();
  const priceLabel = price > 0 ? dollars(price) : (isRsvpGoal ? 'FREE' : null);
  const meta = [place, shortDate(event.date || event.dateTime), priceLabel].filter(Boolean).join(' · ');

  if (funded) {
    return {
      variant: 'soldout',
      init: (host[0] || 'R').toUpperCase(),
      topline: `${host}’s party is `,
      keyword: 'HAPPENING',
      words: isRsvpGoal ? 'Happening.' : 'Funded.',
      meta,
      number: isRsvpGoal ? `${inCount} IN. FLOOR MET.` : `${inCount} GUESTS. GOAL MET.`,
      line: isRsvpGoal ? 'FILLED BY THE PEOPLE COMING.' : 'FUNDED BY THE PEOPLE COMING.',
    };
  }
  const pct = isRsvpGoal
    ? (goalGuests > 0 ? `${Math.min(100, Math.round((inCount / goalGuests) * 100))}%` : null)
    : (goalCents > 0 ? `${Math.min(100, Math.round((raisedCents / goalCents) * 100))}%` : null);
  let line = 'HAPPENS ONLY IF IT HITS THE FLOOR.';
  if (goalGuests > 0) {
    const by = event.fundingDeadline ? ` BY ${shortDate(event.fundingDeadline)}` : '';
    line = isRsvpGoal
      ? `HAPPENS ONLY IF ${goalGuests} ARE IN${by}.`
      : `HAPPENS ONLY IF IT HITS ${goalGuests}${by}.`;
  }
  return {
    variant: 'host',
    init: (host[0] || 'R').toUpperCase(),
    topline: `${host} is starting a `,
    keyword: 'PARTY',
    words: event.title || '',
    meta,
    pct,
    frac: goalGuests ? `${inCount}/${goalGuests}` : null,
    number: away > 0 ? `${inCount} IN. ONLY ${away} AWAY.` : `${inCount} IN.`,
    line,
    // No money moves on a free Party Starter — pink "I'M IN" instead of the red PLEDGE.
    cta: { label: isRsvpGoal ? 'I’M IN' : `PLEDGE ${dollars(price)}` },
  };
}

/**
 * The Party Starter share moment (Phase 2). Renders the pink 1080×1920 Story card
 * for an event and shares it as an image (Instagram Story / WhatsApp / native
 * sheet) or copies the link. Mirrors SignalShareModal's raster pipeline.
 */
export default function PartyStarterShareModal({ event, onClose }) {
  const captureRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => { ensureFont(); }, []);

  const defaults = useShareTemplates();
  const [picked, setPicked] = useState(null);
  const template = picked || defaults?.story || 'ring';
  const card = { ...buildCard(event), template };
  const url = `${webOrigin()}/events/${event.inviteCode || event.id}`;
  const shareText = card.variant === 'soldout'
    ? `${card.topline}${card.keyword}. See what's starting next on Portal.`
    : `${card.topline}${card.keyword}: “${card.words}” — ${event.goalType === 'rsvp' ? "tap I'm in" : 'pledge'} on Portal`;

  const makeBlob = async () => {
    ensureFont();
    try { await document.fonts.load("700 33px 'Courier Prime'"); await document.fonts.ready; } catch { /* fonts optional */ }
    await new Promise((r) => setTimeout(r, 80));
    return htmlToImage.toBlob(captureRef.current, { width: 1080, height: 1920, pixelRatio: 1, cacheBust: true, backgroundColor: '#000' });
  };

  const shareImage = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const blob = await makeBlob();
      if (!blob) throw new Error('render failed');
      // navigator.share is absent in Android's WebView and inert in a
      // plain WKWebView; shareImageFile asks the OS directly when native.
      await shareImageFile({ blob, fileName: 'party-resume-party.png', title: 'Portal', text: shareText, url });
    } catch { /* cancelled / failed */ }
    setBusy(false);
  };

  const shareWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(`${shareText} ${url}`)}`, '_blank', 'noopener');
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* blocked */ }
  };

  const btn = 'h-12 border-2 text-[13px] font-bold uppercase tracking-[0.2em] transition-opacity disabled:opacity-60';

  return createPortal(
    <div className="pr-overlay-root fixed inset-0 safe-inset z-[80] flex items-end justify-center bg-black/70 sm:items-center" onClick={onClose}>
      <div
        className="w-full max-w-sm border-t-2 border-black bg-black p-6 sm:border-2 sm:border-white/20"
        style={{ fontFamily: "'Courier Prime', ui-monospace, monospace" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-[10px] font-bold uppercase tracking-[0.32em]" style={{ color: PINK }}>[ Live ]</div>
        <div className="mt-2 text-[15px] font-bold uppercase leading-snug text-white">
          {card.variant === 'soldout' ? <>Goal met.<br />Take a bow?</> : <>Party&apos;s brewing.<br />Rally the floor?</>}
        </div>

        <div className="my-5 flex justify-center">
          <div style={{ width: 200, height: 356, overflow: 'hidden', outline: '1px solid #333' }}>
            <div style={{ transform: 'scale(0.18518)', transformOrigin: 'top left' }}>
              <PartyStarterStoryCard {...card} />
            </div>
          </div>
        </div>

        <div className="mb-4 flex justify-center">
          <ShareTemplatePicker
            kind="party_starter"
            value={template}
            onChange={setPicked}
            size={64}
            renderPreview={(t) => <ScaledCard box={60}><PartyStarterStoryCard {...card} template={t} /></ScaledCard>}
          />
        </div>

        <div className="flex flex-col gap-2">
          <button type="button" onClick={shareImage} disabled={busy} className={`${btn} text-black`} style={{ background: PINK, borderColor: PINK }}>
            {busy ? 'Rendering…' : 'Instagram Story'}
          </button>
          <button type="button" onClick={shareWhatsApp} className={`${btn} border-white bg-transparent text-white`}>
            WhatsApp
          </button>
          <button type="button" onClick={copyLink} className={`${btn} border-white/40 bg-transparent text-white/80`}>
            {copied ? 'Copied' : 'Copy Link'}
          </button>
        </div>
        <button type="button" onClick={onClose} className="mt-4 w-full text-center text-[13px] tracking-wide text-white/45 hover:text-white">not now</button>
      </div>

      <div style={{ position: 'fixed', left: -99999, top: 0, pointerEvents: 'none' }} aria-hidden>
        <PartyStarterStoryCard {...card} refEl={captureRef} />
      </div>
    </div>,
    document.body,
  );
}
